import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';
import type { PerformanceScore } from '@/types/performance-profile';

import { levelForScore } from '@/utils/performanceScoreLevel';

interface CalculatedLevelGapParams {
    score: PerformanceScore;
    /** The company's scale, the very list the server resolves a score against. */
    levels: PerformanceScoreLevel[];
}

/**
 * The level the score reaches, when the dial is naming another one. A manager who kept a level
 * themselves overrides the scale, and the arc keeps being drawn from the score: without this, the
 * word and the ring state two different things on one drawing and nothing says so.
 *
 * `null` whenever there is nothing to contrast -- the word on screen already is the calculated
 * one, there is no score, the scale reaches no level, or the two levels cannot be told apart,
 * where "they differ" would be a guess rather than a reading.
 */
export const calculatedLevelGap = ({
    score,
    levels,
}: CalculatedLevelGapParams): PerformanceScoreLevel | null => {
    if (score.levelSource !== 'ManagerChosen' || score.score == null) {
        return null;
    }

    const calculated = levelForScore({ score: score.score, levels });
    if (!calculated?.id || !score.level?.id || calculated.id === score.level.id) {
        return null;
    }

    return calculated;
};
